/**
 * useItemPermission — 메뉴 내 필드/아이템 단위 가시성·편집 제어 훅.
 *
 * 【역할】
 * - usePermission 이 "메뉴 × 액션" 이라면, 이 훅은 "메뉴 × 필드 × 레벨".
 * - 급여/민감정보 등 특정 필드만 readonly/hidden 처리하는 fine-grained 권한.
 *
 * 【주요 기능】
 * - load(): /auth/item-permissions/{menuKey} 조회 후 store 주입
 * - getLevel(itemKey): 'hidden' | 'readonly' | 'editable' (기본 editable)
 * - isVisible(itemKey): 'hidden' 외 모두 true
 * - isEditable(itemKey): 'editable' 만 true
 *
 * 【내부 상태 / 캐시 구조】
 * - Zustand store: `map[menuKey][itemKey] = level`. 메뉴별로 페이지 마운트 시 load().
 *
 * 【사용 예시】
 * ```tsx
 * const perm = useItemPermission('USER_MGMT')
 * useEffect(() => { void perm.load() }, [])
 * {perm.isVisible('salary') && <Form.Item><Input disabled={!perm.isEditable('salary')} /></Form.Item>}
 * ```
 *
 * 【왜 이 패턴?】
 * - HR/재무 도메인에서 "조회 권한은 있지만 수정 불가" 같은 세분화된 요구에 대응.
 */
import { useCallback } from 'react'
import { create } from 'zustand'
import http from './api/axios'

/** 아이템 권한 레벨 — hidden(완전 비표시) / readonly(표시·편집불가) / editable(완전 권한). */
export type ItemPermLevel = 'hidden' | 'readonly' | 'editable'

/** Zustand store 형상 — 메뉴키별 itemKey→level 맵을 중첩 보관. */
interface ItemPermState {
  /** menuKey → (itemKey → level) — 페이지 단위 캐시 */
  map: Record<string, Record<string, ItemPermLevel>>
  /** 특정 메뉴의 권한 맵을 통째로 교체 (load 결과 주입용) */
  setPage: (menuKey: string, items: Record<string, ItemPermLevel>) => void
}

const useStore = create<ItemPermState>((set) => ({
  map: {},
  setPage: (menuKey, items) => set((s) => ({ map: { ...s.map, [menuKey]: items } }))
}))

/**
 * useItemPermission — 메뉴 단위 아이템 권한 훅.
 * @param menuKey - 페이지를 식별하는 메뉴 키 (예: 'USER_MGMT'). 권한 맵이 이 키 하위에 적재된다.
 * @returns load/getLevel/isVisible/isEditable + 현재 items 맵.
 */
export function useItemPermission(menuKey: string) {
  const items = useStore((s) => s.map[menuKey] ?? {})
  const setPage = useStore((s) => s.setPage)

  /**
   * 백엔드에서 현재 메뉴의 아이템 권한을 조회 후 store 에 주입.
   * 실패 시 빈 맵으로 설정 → getLevel 은 모두 기본값 'editable' 반환 (보수적 허용).
   */
  const load = useCallback(async () => {
    try {
      const res = await http.get(`/auth/item-permissions/${menuKey}`)
      const data = res.data?.data ?? res.data ?? {}
      setPage(menuKey, data)
    } catch {
      setPage(menuKey, {})
    }
  }, [menuKey, setPage])

  /**
   * 아이템의 권한 레벨 조회 — 미정의 키는 기본 'editable'.
   * @param itemKey - 필드/버튼 식별자.
   */
  const getLevel = useCallback(
    (itemKey: string): ItemPermLevel => items[itemKey] ?? 'editable',
    [items]
  )

  /** 화면 표시 가능 여부 — 'hidden' 만 false. */
  const isVisible = useCallback((itemKey: string) => getLevel(itemKey) !== 'hidden', [getLevel])

  /** 입력/편집 가능 여부 — 'editable' 만 true. */
  const isEditable = useCallback((itemKey: string) => getLevel(itemKey) === 'editable', [getLevel])

  return { load, getLevel, isVisible, isEditable, items }
}
